import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { BarChart3, Star, TrendingUp, ThumbsUp, XCircle, Clock, MessageSquare } from "lucide-react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock performance trend
const performanceTrend = [
  { month: "Jul", score: 78 },
  { month: "Aug", score: 80 },
  { month: "Sep", score: 82 },
  { month: "Oct", score: 85 },
  { month: "Nov", score: 83 },
  { month: "Dec", score: 87 },
];

// Mock feedback
const patientFeedback = [
  { id: 1, patient: "Ahmad R.", rating: 5, comment: "Very professional and caring!", date: "2024-12-28" },
  { id: 2, patient: "Sarah L.", rating: 5, comment: "Excellent treatment, feeling much better.", date: "2024-12-25" },
  { id: 3, patient: "Kumar R.", rating: 4, comment: "Good session, helpful exercises.", date: "2024-12-22" },
];

export default function TherapistPerformance() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/therapist/stats`, { withCredentials: true });
        setStats(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const radarData = [
    { metric: "Rating", value: (stats?.rating || 0) * 20, fullMark: 100 },
    { metric: "Acceptance", value: stats?.acceptance_rate || 0, fullMark: 100 },
    { metric: "Reliability", value: 100 - (stats?.cancellation_rate || 0), fullMark: 100 },
    { metric: "Punctuality", value: 100 - ((stats?.no_show_rate || 0) * 2), fullMark: 100 },
    { metric: "LMS Progress", value: stats?.lms_completion || 0, fullMark: 100 },
  ];

  return (
    <div className="space-y-6" data-testid="therapist-performance">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Performance Score</p>
                <p className="font-heading font-bold text-xl text-slate-900">{stats?.performance_score}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Patient Rating</p>
                <p className="font-heading font-bold text-xl text-slate-900">{stats?.rating?.toFixed(1)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <ThumbsUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Acceptance Rate</p>
                <p className="font-heading font-bold text-xl text-slate-900">{stats?.acceptance_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Cancellation Rate</p>
                <p className="font-heading font-bold text-xl text-slate-900">{stats?.cancellation_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Performance Trend */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Performance Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={performanceTrend}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="month" />
                <YAxis domain={[70, 100]} />
                <Tooltip 
                  formatter={(value) => [`${value}%`, 'Score']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#F40d07" 
                  strokeWidth={3}
                  dot={{ fill: '#F40d07', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Radar Chart */}
        <Card className="chart-container">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Performance Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 12 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar
                  name="Performance"
                  dataKey="value"
                  stroke="#F40d07"
                  fill="#F40d07"
                  fillOpacity={0.2}
                />
                <Tooltip 
                  formatter={(value) => [`${value.toFixed(0)}%`, '']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Metrics Detail */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Detailed Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <ThumbsUp className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="font-medium">Acceptance Rate</p>
                  <p className="text-sm text-slate-500">Rate of accepting session requests</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Progress value={stats?.acceptance_rate || 0} className="w-24 h-2" />
                <span className="font-bold text-emerald-600">{stats?.acceptance_rate}%</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <XCircle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="font-medium">Cancellation Rate</p>
                  <p className="text-sm text-slate-500">Sessions cancelled by you</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Progress value={stats?.cancellation_rate || 0} className="w-24 h-2" />
                <span className={`font-bold ${(stats?.cancellation_rate || 0) <= 5 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {stats?.cancellation_rate}%
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-medium">No-Show Rate</p>
                  <p className="text-sm text-slate-500">Sessions missed without notice</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Progress value={(stats?.no_show_rate || 0) * 10} className="w-24 h-2" />
                <span className={`font-bold ${(stats?.no_show_rate || 0) <= 3 ? 'text-emerald-600' : 'text-red-600'}`}>
                  {stats?.no_show_rate}%
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Patient Feedback */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-primary" />
            Recent Patient Feedback
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {patientFeedback.map((feedback) => (
              <div key={feedback.id} className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{feedback.patient}</span>
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`w-4 h-4 ${i < feedback.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} 
                        />
                      ))}
                    </div>
                  </div>
                  <span className="text-sm text-slate-500">{feedback.date}</span>
                </div>
                <p className="text-slate-600">"{feedback.comment}"</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Improvement Suggestions */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Improvement Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-lg">
              <p className="font-medium text-emerald-800">Complete Advanced Training</p>
              <p className="text-sm text-emerald-600">Finishing the "Manual Therapy" module will boost your score by 5%</p>
            </div>
            <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg">
              <p className="font-medium text-blue-800">Maintain Consistency</p>
              <p className="text-sm text-blue-600">Keep your acceptance rate above 90% for tier promotion</p>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="font-medium text-amber-800">Collect More Reviews</p>
              <p className="text-sm text-amber-600">Remind patients to leave feedback after successful sessions</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
